import React from 'react';
import { Check, Trash2, X } from 'lucide-react';
import { AutoResizeTextarea } from '../../../components/AutoResizeTextarea';
import { APP_BTN_OUTLINE_CLASS, APP_BTN_PRIMARY_CLASS, APP_TEXTAREA_CLASS } from '../../../ui/controlStyles';
import { memberName, taskStatus } from '../taskViews';
import type { Task, TaskLeadOption, TaskMember, TaskPatch } from '../types';
import type { TasksApi } from '../useTasks';
import { TaskChecklist } from './TaskChecklist';
import { PanelIconButton, TASK_TITLE_INPUT_CLASS, TaskFields, TaskPanelFrame } from './TaskFields';
import { StatusPill, TASK_SECTION_LABEL_CLASS } from './taskUi';

type Props = {
  task: Task;
  today: string;
  members: TaskMember[];
  membersById: Map<string, TaskMember>;
  leadOptions: TaskLeadOption[];
  instancePrefix: string;
  canEdit: boolean;
  canOpenProjects: boolean;
  /** Über der Liste schwebend statt daneben */
  overlay: boolean;
  tasksApi: TasksApi;
  onClose: () => void;
};

function formatCreated(task: Task, membersById: Map<string, TaskMember>): string {
  const d = new Date(task.created_at);
  const when = `${d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}, ${d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`;
  const who = task.created_by ? membersById.get(task.created_by) : null;
  return `Erstellt am ${when}${who ? ` · ${memberName(who)}` : ''}`;
}

/** Entwurf, der bei Fremdänderungen nachzieht, solange das Feld nicht fokussiert ist. */
function useDraft(value: string, resetKey: string) {
  const [draft, setDraft] = React.useState(value);
  const focused = React.useRef(false);
  React.useEffect(() => {
    setDraft(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- nur beim Wechsel der Aufgabe zurücksetzen
  }, [resetKey]);
  React.useEffect(() => {
    if (!focused.current) setDraft(value);
  }, [value]);
  return {
    draft,
    setDraft,
    bind: {
      onFocus: () => {
        focused.current = true;
      },
      onBlurBase: () => {
        focused.current = false;
      },
    },
  };
}

export function TaskDetailPanel({
  task,
  today,
  members,
  membersById,
  leadOptions,
  instancePrefix,
  canEdit,
  canOpenProjects,
  overlay,
  tasksApi,
  onClose,
}: Props) {
  const title = useDraft(task.title, task.id);
  const description = useDraft(task.description, task.id);

  const patch = (p: TaskPatch, lead?: Task['lead']) => void tasksApi.update(task.id, p, lead);

  /**
   * Ungespeicherte Eingaben — beim Wechsel der Aufgabe oder Schließen des Panels feuert kein Blur,
   * deshalb sichert der Cleanup-Effekt unten, was hier noch offen ist.
   */
  const dirtyRef = React.useRef<{ taskId: string; title: string | null; description: string | null }>({
    taskId: task.id,
    title: null,
    description: null,
  });
  const updateRef = React.useRef(tasksApi.update);
  updateRef.current = tasksApi.update;
  React.useEffect(() => {
    dirtyRef.current = { taskId: task.id, title: null, description: null };
    return () => {
      const { taskId, title: t, description: d } = dirtyRef.current;
      const p: TaskPatch = {};
      const cleanTitle = t?.replace(/\s+/g, ' ').trim();
      if (cleanTitle) p.title = cleanTitle;
      if (d !== null) p.description = d;
      if (Object.keys(p).length) void updateRef.current(taskId, p);
    };
  }, [task.id]);

  const commitTitle = () => {
    title.bind.onBlurBase();
    dirtyRef.current.title = null;
    const next = title.draft.replace(/\s+/g, ' ').trim();
    if (!next) {
      title.setDraft(task.title);
      return;
    }
    if (next !== task.title) patch({ title: next });
  };

  const commitDescription = () => {
    description.bind.onBlurBase();
    dirtyRef.current.description = null;
    if (description.draft !== task.description) patch({ description: description.draft });
  };

  const status = taskStatus(task, today);

  return (
    <TaskPanelFrame
      overlay={overlay}
      onBack={onClose}
      label="To-do"
      header={
        <>
          <span className="font-mono text-[11px] text-app-text-subtle">{task.display_id}</span>
          <div className="flex gap-1">
            {canEdit && (
              <PanelIconButton title="To-do löschen" danger onClick={() => void tasksApi.remove(task.id)}>
                <Trash2 className="h-4 w-4" strokeWidth={1.8} />
              </PanelIconButton>
            )}
            <PanelIconButton title="Schließen (Esc)" onClick={onClose} className="max-md:hidden">
              <X className="h-4 w-4" strokeWidth={1.8} />
            </PanelIconButton>
          </div>
        </>
      }
      footer={
        <div className="shrink-0 px-5 py-3 text-xs leading-relaxed text-app-text-subtle">
          {formatCreated(task, membersById)}
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        <AutoResizeTextarea
          value={title.draft}
          readOnly={!canEdit}
          rows={1}
          aria-label="Titel"
          onFocus={title.bind.onFocus}
          onChange={(e) => {
            title.setDraft(e.target.value);
            dirtyRef.current.title = e.target.value;
          }}
          onBlur={commitTitle}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              e.currentTarget.blur();
            }
          }}
          className={TASK_TITLE_INPUT_CLASS}
        />
        {canEdit && (
          <button
            type="button"
            onClick={() => void tasksApi.toggleDone(task.id)}
            className={`${task.done ? APP_BTN_OUTLINE_CLASS : APP_BTN_PRIMARY_CLASS} !h-8 self-start`}
          >
            <Check className="h-3.5 w-3.5" strokeWidth={2.2} />
            {task.done ? 'Wieder öffnen' : 'Als erledigt markieren'}
          </button>
        )}
      </div>

      <TaskFields
        values={task}
        onChange={patch}
        members={members}
        leadOptions={leadOptions}
        canEdit={canEdit}
        canOpenProjects={canOpenProjects}
        instancePrefix={instancePrefix}
        leading={
          <>
            <span className="text-app-text-subtle">Status</span>
            <span className="justify-self-start">
              <StatusPill status={status} />
            </span>
          </>
        }
      />

      <div className="flex flex-col gap-2">
        <span className={TASK_SECTION_LABEL_CLASS}>Beschreibung</span>
        <AutoResizeTextarea
          value={description.draft}
          readOnly={!canEdit}
          rows={3}
          maxHeight={400}
          aria-label="Beschreibung"
          placeholder={canEdit ? 'Hier Beschreibung eingeben…' : 'Keine Beschreibung'}
          onFocus={description.bind.onFocus}
          onChange={(e) => {
            description.setDraft(e.target.value);
            dirtyRef.current.description = e.target.value;
          }}
          onBlur={commitDescription}
          className={`${APP_TEXTAREA_CLASS} resize-none leading-relaxed`}
        />
      </div>

      <TaskChecklist task={task} canEdit={canEdit} tasksApi={tasksApi} />
    </TaskPanelFrame>
  );
}
